import { useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Download, Plus, FileText, Filter, Search, Edit3, Printer } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { DateRangePicker, rangeForPreset, DatePreset } from '@/components/ui/DateRangePicker';
import { CopyText } from '@/components/ui/CopyText';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

interface DebitNote {
  id: number; debitNoteNo: string; orderType: 'B2C'|'B2B'; merchantId: number;
  invoiceCompanyId?: number; invoiceCompanyGstin?: string; invoiceNoAgainst?: string; title?: string; particulars?: string;
  hsnSac?: string;
  subtotal: string; cgstPct?: string; sgstPct?: string; igstPct?: string;
  cgstAmount: string; sgstAmount: string; igstAmount: string;
  totalAmount: string; totalInWords?: string;
  notes?: string; status: string; debitDate: string; createdAt: string;
  merchant?: { id: number; companyName: string; gstin?: string };
}

export function DebitNotesPage({ orderType }: { orderType?: 'B2C'|'B2B' }) {
  const qc = useQueryClient();
  const combined = !orderType; // no orderType → one merged list of B2C + B2B debit notes
  const [debitNoteNo, setDebitNoteNo] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const { user: _au } = useAuth();
  const isMerchant = _au?.kind === 'merchant';
  const initial = rangeForPreset('this-month');
  const [start, setStart] = useState(initial.startDate);
  const [end, setEnd] = useState(initial.endDate);
  const [preset, setPreset] = useState<DatePreset>('this-month');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<DebitNote | null>(null);
  const cols = (isMerchant ? 6 : 7) + (combined ? 1 : 0);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['debit-notes', { orderType, debitNoteNo, merchantId, start, end, page }],
    queryFn: async () => {
      const params: any = { page, pageSize: 50, startDate: start, endDate: end };
      if (orderType) params.orderType = orderType;
      if (debitNoteNo) params.debitNoteNo = debitNoteNo;
      if (merchantId) params.merchantId = merchantId;
      return (await api.get<{ total: number; items: DebitNote[] }>('/debit-notes', { params })).data;
    },
  });

  const { data: merchants } = useQuery({
    queryKey: ['merchants-light'],
    queryFn: async () => (await api.get<any[]>('/merchants', { params: { pageSize: 500 } })).data,
  });
  const merchantList = useMemo(() => Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? [], [merchants]);

  function exportCsv() {
    const qs = new URLSearchParams();
    if (orderType) qs.set('orderType', orderType);
    if (start) qs.set('startDate', start);
    if (end) qs.set('endDate', end);
    downloadBlob(`/debit-notes/export?${qs.toString()}`, `debit-notes-${orderType ?? 'all'}-${new Date().toISOString().slice(0,10)}.csv`);
  }

  function openPdf(id: number) {
    const w = window.open('', '_blank');
    api.get(`/debit-notes/${id}/pdf`, { responseType: 'text' })
      .then(r => { if (w) { w.document.open(); w.document.write(r.data as string); w.document.close(); } })
      .catch(() => w?.close());
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileText size={22} /> Debit Notes {orderType ?? ''}</h1>
          <p className="text-sm text-slate-500">Issue, edit and download debit notes (GST compliant)</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
            <Download size={14} /> Export Debit Note[s]
          </button>
          {/* Merchants can view/export debit notes but not issue them (admin-only). */}
          {!isMerchant && (
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90">
              <Plus size={14} /> Generate Debit Note
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 grid grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Debit Note No</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={debitNoteNo} onChange={(e) => setDebitNoteNo(e.target.value)} placeholder="Enter Debit Note Number Here" className="input h-10 pl-9 w-full" />
          </div>
        </div>
        {!isMerchant && (
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Merchants</label>
          <SearchableSelect
            value={merchantId}
            onChange={(v) => setMerchantId(v === '' ? '' : String(v))}
            options={merchantList.map((m: any) => ({ value: String(m.id), label: `${m.companyName} (#${m.id})` }))}
            placeholder="Select Merchants"
          />
        </div>
        )}
        <div className="col-span-2">
          <label className="text-xs text-slate-500 mb-1 block">Date Range</label>
          <DateRangePicker value={{ startDate: start, endDate: end }} defaultPreset={preset}
            onChange={(r) => { setStart(r.startDate); setEnd(r.endDate); setPreset(r.preset); setPage(1); }} />
        </div>
        <div className="col-span-4 flex items-center gap-2">
          <button onClick={() => { setPage(1); refetch(); }} className="px-4 py-2 bg-primary text-white rounded text-sm">Search</button>
          <button onClick={() => { setDebitNoteNo(''); setMerchantId(''); const t = rangeForPreset('this-month'); setStart(t.startDate); setEnd(t.endDate); setPreset('this-month'); }}
            className="px-4 py-2 border border-slate-200 text-sm rounded">Reset</button>
          <div className="ml-auto text-xs text-slate-500"><Filter size={12} className="inline mr-1" /> {data?.total ?? 0} debit notes</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Debit No.</th>
                {combined && <th className="text-left px-4 py-2.5">Service</th>}
                {!isMerchant && <th className="text-left px-4 py-2.5">Merchant Name</th>}
                <th className="text-left px-4 py-2.5">GST No.</th>
                <th className="text-left px-4 py-2.5">Debit Date</th>
                <th className="text-right px-4 py-2.5">Debit Amount</th>
                <th className="text-left px-4 py-2.5">Created At</th>
                <th className="text-center px-4 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={cols} className="text-center py-10"><Loader2 size={20} className="animate-spin mx-auto" /></td></tr>)}
              {!isLoading && (data?.items ?? []).length === 0 && (<tr><td colSpan={cols} className="text-center py-12 text-slate-400">No debit notes for these filters.</td></tr>)}
              {(data?.items ?? []).map(c => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-2 font-mono text-sm"><CopyText value={c.debitNoteNo} display={<span>{c.debitNoteNo}</span>} iconSize={10} silent /></td>
                  {combined && <td className="px-4 py-2"><span className={`text-xs px-1.5 py-0.5 rounded ${c.orderType === 'B2B' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>{c.orderType}</span></td>}
                  {!isMerchant && (
                  <td className="px-4 py-2 text-sm">
                    <div className="inline-flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center font-bold">{(c.merchant?.companyName ?? '?').charAt(0)}</div>{c.merchant?.companyName ?? '—'}</div>
                  </td>
                  )}
                  <td className="px-4 py-2 font-mono text-xs">{c.merchant?.gstin ?? '—'}</td>
                  <td className="px-4 py-2 text-xs">{new Date(c.debitDate).toLocaleDateString('en-GB').replace(/\//g, '-')}</td>
                  {/* A payable total is a whole rupee — the paise live on the subtotal and tax lines. */}
                  <td className="px-4 py-2 text-right font-mono">{Math.round(Number(c.totalAmount)).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2 text-xs">{new Date(c.createdAt).toLocaleDateString('en-GB').replace(/\//g, '-')}</td>
                  <td className="px-4 py-2 text-center">
                    <button onClick={() => openPdf(c.id)} title="Download PDF" className="text-xs px-2 py-1 border border-rose-200 text-rose-600 rounded hover:bg-rose-50 inline-flex items-center gap-1"><Printer size={11} /> PDF</button>
                    {/* Editing re-opens the issue form → merchants (view/export only) don't get it. */}
                    {!isMerchant && (
                      <button onClick={() => { setEditing(c); setShowForm(true); }} title="Edit" className="ml-1 text-xs px-2 py-1 border border-slate-200 text-slate-700 rounded hover:bg-slate-50 inline-flex items-center gap-1"><Edit3 size={11} /> Edit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <Modal isOpen={true} onClose={() => { setShowForm(false); setEditing(null); }}
          title={editing ? 'Edit Debit Note' : 'Add Debit Note'} maxWidth="3xl">
          <DebitNoteForm initial={editing} orderType={orderType} merchants={merchantList} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); setEditing(null); qc.invalidateQueries({ queryKey: ['debit-notes'] }); }} />
        </Modal>
      )}
    </div>
  );
}

function DebitNoteForm({ initial, orderType, merchants, onClose, onSaved }: { initial: DebitNote | null; orderType?: 'B2C'|'B2B'; merchants: any[]; onClose: () => void; onSaved: () => void }) {
  const [serviceType, setServiceType] = useState<'B2C'|'B2B'>(initial?.orderType ?? orderType ?? 'B2C');
  const [debitNoteNo, setDebitNoteNo] = useState(initial?.debitNoteNo ?? '');
  const [invoiceCompanyGstin, setInvoiceCompanyGstin] = useState<string>(initial?.invoiceCompanyGstin ?? '');
  const [merchantId, setMerchantId] = useState<number | ''>(initial?.merchantId ?? '');
  const [invoiceNoAgainst, setInvoiceNoAgainst] = useState(initial?.invoiceNoAgainst ?? '');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [subtotal, setSubtotal] = useState<number>(Number(initial?.subtotal ?? 0));
  const [cgstPct, setCgstPct] = useState<number>(Number(initial?.cgstPct ?? 0));
  const [sgstPct, setSgstPct] = useState<number>(Number(initial?.sgstPct ?? 0));
  const [igstPct, setIgstPct] = useState<number>(Number(initial?.igstPct ?? 0));
  const [debitDate, setDebitDate] = useState(initial?.debitDate?.slice(0,10) ?? new Date().toISOString().slice(0,10));
  const [hsnSac, setHsnSac] = useState(initial?.hsnSac ?? '996812');
  const [notes, setNotes] = useState(initial?.notes ?? '');

  // Invoice / GST companies are stored under settings key `invoice_companies` as { items: [...] }
  // with snake_case fields (company_name, company_gstin) and no numeric id — so we key by GSTIN.
  const { data: gstCompanies } = useQuery({
    queryKey: ['gst-companies'],
    queryFn: async () => {
      try {
        const r: any = (await api.get('/settings/invoice_companies')).data;
        const arr = Array.isArray(r) ? r : (r?.items ?? []);
        return (arr as any[]).filter((c) => c && c.company_gstin && c.isActive !== false);
      } catch { return []; }
    },
  });

  const total = useMemo(() => {
    const t = subtotal + (subtotal * cgstPct) / 100 + (subtotal * sgstPct) / 100 + (subtotal * igstPct) / 100;
    return Math.round(t * 100) / 100;
  }, [subtotal, cgstPct, sgstPct, igstPct]);

  const merchant = useMemo(() => merchants.find((m: any) => m.id === Number(merchantId)), [merchants, merchantId]);
  const merchantGst = (merchant?.billing?.gstNumber) ?? merchant?.gstin ?? '';

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        debitNoteNo, orderType: serviceType, merchantId: Number(merchantId),
        invoiceCompanyGstin: invoiceCompanyGstin || null,
        invoiceNoAgainst, title, hsnSac, particulars: title,
        subtotal, cgstPct, sgstPct, igstPct,
        debitDate, notes,
      };
      if (initial) return (await api.patch(`/debit-notes/${initial.id}`, payload)).data;
      return (await api.post('/debit-notes', payload)).data;
    },
    onSuccess: () => { toast.success(initial ? 'Debit note updated' : 'Debit note created'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Service Type *</label>
          <select value={serviceType} onChange={(e) => setServiceType(e.target.value as 'B2C'|'B2B')} disabled={!!initial} className="input w-full">
            <option value="B2C">B2C</option>
            <option value="B2B">B2B</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Select GST No.</label>
          <select value={invoiceCompanyGstin} onChange={(e) => setInvoiceCompanyGstin(e.target.value)} className="input w-full">
            <option value="">Choose seller</option>
            {(gstCompanies ?? []).map((g: any) => <option key={g.company_gstin} value={g.company_gstin}>{g.company_name} ({g.company_gstin})</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Select Merchant *</label>
          <SearchableSelect
            value={merchantId}
            onChange={(v) => setMerchantId(v === '' ? '' : Number(v))}
            options={merchants.map((m: any) => ({ value: m.id, label: `${m.companyName} (#${m.id})` }))}
            placeholder="Choose merchant"
            disabled={!!initial}
          />
          {merchantGst && <div className="text-xs text-slate-500 mt-1">Merchant GST: <span className="font-mono">{merchantGst}</span></div>}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Debit Note No *</label>
          <input value={debitNoteNo} onChange={(e) => setDebitNoteNo(e.target.value)} disabled={!!initial} className="input w-full" placeholder="DRM/DN/024" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Debit Note Title *</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="input w-full" placeholder="e.g. DRM/124/2026-27 Correction" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Invoice No</label>
          <input value={invoiceNoAgainst} onChange={(e) => setInvoiceNoAgainst(e.target.value)} className="input w-full" placeholder="DRM/124" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Subtotal *</label>
          <input type="number" value={subtotal} onChange={(e) => setSubtotal(Number(e.target.value))} className="input w-full" step="0.01" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">CGST %</label>
          <input type="number" value={cgstPct} onChange={(e) => setCgstPct(Number(e.target.value))} className="input w-full" step="0.1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">SGST %</label>
          <input type="number" value={sgstPct} onChange={(e) => setSgstPct(Number(e.target.value))} className="input w-full" step="0.1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">IGST %</label>
          <input type="number" value={igstPct} onChange={(e) => setIgstPct(Number(e.target.value))} className="input w-full" step="0.1" />
          <div className="text-xs text-slate-500 mt-1">Total: <b>{total.toFixed(2)}</b></div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Debit Note Date *</label>
          <input type="date" value={debitDate} onChange={(e) => setDebitDate(e.target.value)} className="input w-full" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">HSN/SAC</label>
          <input value={hsnSac} onChange={(e) => setHsnSac(e.target.value)} className="input w-full" />
        </div>
        <div className="col-span-1">
          <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Notes / Terms</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} className="input w-full" />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">CANCEL</button>
        <button onClick={() => save.mutate()} disabled={save.isPending || !merchantId || !debitNoteNo || !debitDate || !title}
          className="px-5 py-2 bg-primary text-white text-sm rounded hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
          {save.isPending ? <Loader2 size={14} className="animate-spin" /> : null} Submit
        </button>
      </div>
    </div>
  );
}

export function DebitNotesB2cPage() { return <DebitNotesPage orderType="B2C" />; }
export function DebitNotesB2bPage() { return <DebitNotesPage orderType="B2B" />; }
